import {
  addDoc,
  collection,
  deleteDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { useEffect, useState } from "react";

import { UserOrg, UserOrgNew, Uid } from "@/types";

import { createDocumentConverter } from "./fetchDoc";
import { db } from "../firebase/clientApp";
import { useFetchCollection } from "../hooks/useFetchCollection";

export const USER_ORG_CONVERTER = createDocumentConverter<UserOrg>();

// HOOKS

/**
 * Hook to fetch all user-org relationships for a specific organization
 *
 * @param orgUid - Organization UID to filter by
 * @returns All user-org relationships for the specified org with loading/error states
 *
 * @example
 * const { data: userOrgs, loading } = useUserOrgs('ohc400');
 */
export const useUserOrgs = (orgUid: string) => {
  return useFetchCollection({
    path: "jUserOrgs",
    converter: USER_ORG_CONVERTER,
    realtime: true,
    query: where("orgUid", "==", orgUid),
    source: "useUserOrgs",
  });
};

/**
 * Hook to fetch a specific user-org relationship
 *
 * @param userUid - User UID to fetch relationship for
 * @param orgUid - Organization UID to fetch relationship for
 * @returns User-org relationship with loading/error states
 *
 * @example
 * const { data: userOrg, loading } = useUserOrg('user123', 'ohc400');
 */
export const useUserOrg = (userUid: Uid, orgUid: string) => {
  const [data, setData] = useState<UserOrg[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserOrg = async () => {
      try {
        setLoading(true);
        setError(null);

        const querySnapshot = await getDocs(
          query(
            collection(db, "jUserOrgs").withConverter(USER_ORG_CONVERTER),
            where("userUid", "==", userUid),
            where("orgUid", "==", orgUid),
          ),
        );

        const userOrgs = querySnapshot.docs.map((doc) => doc.data());
        setData(userOrgs);
      } catch (err) {
        setError(`Failed to load user-org relationship. ${err}`);
      } finally {
        setLoading(false);
      }
    };

    fetchUserOrg();
  }, [userUid, orgUid]);

  return { data, loading, error };
};

// CRUD OPERATIONS

/**
 * Get a user-org relationship by user UID and org UID
 *
 * @param userUid - User UID
 * @param orgUid - Organization UID
 * @returns User-org relationship or null if not found
 */
export const getUserOrgRelationship = async (
  userUid: Uid,
  orgUid: string,
): Promise<UserOrg | null> => {
  const querySnapshot = await getDocs(
    query(
      collection(db, "jUserOrgs").withConverter(USER_ORG_CONVERTER),
      where("userUid", "==", userUid),
      where("orgUid", "==", orgUid),
    ),
  );

  if (querySnapshot.empty) {
    return null;
  }

  return querySnapshot.docs[0].data();
};

/**
 * Add a user to an organization
 *
 * @param userUid - User UID
 * @param orgUid - Organization UID
 * @param role - User role in the organization
 * @param permissions - Array of permissions
 * @param notes - Optional notes
 * @returns Promise with the created document reference
 *
 * @example
 * await addUserToOrg('user123', 'ohc400', 'parent', ['read'], 'Added via admin panel');
 */
export const addUserToOrg = async (
  userUid: Uid,
  orgUid: string,
  role: string,
  permissions: string[] = ["read"],
  notes?: string,
) => {
  const userOrgData: UserOrgNew = {
    userUid,
    orgUid,
    roles: { [role]: true }, // Convert single role to roles object
    createdAt: new Date(), // Will be converted to Timestamp
    // Legacy fields for backward compatibility
    role,
    permissions,
    joinedAt: new Date(), // Will be converted to Timestamp
    status: "active",
    notes,
  };

  return addDoc(collection(db, "jUserOrgs"), userOrgData);
};

/**
 * Update a user's role or permissions in an organization
 *
 * @param userOrg - Updated user-org relationship data
 * @returns Promise with the update result
 */
export const updateUserOrg = async (userOrg: UserOrg) => {
  // Find the document by userUid and orgUid
  const querySnapshot = await getDocs(
    query(
      collection(db, "jUserOrgs"),
      where("userUid", "==", userOrg.userUid),
      where("orgUid", "==", userOrg.orgUid),
    ),
  );

  if (querySnapshot.empty) {
    throw new Error("User-org relationship not found");
  }

  const docRef = querySnapshot.docs[0].ref;
  return updateDoc(docRef, userOrg);
};

/**
 * Remove a user from an organization
 *
 * @param userUid - User UID to remove
 * @param orgUid - Organization UID to remove user from
 * @returns Promise with the deletion result
 */
export const removeUserFromOrg = async (userUid: Uid, orgUid: string) => {
  // Find the document by userUid and orgUid
  const querySnapshot = await getDocs(
    query(
      collection(db, "jUserOrgs"),
      where("userUid", "==", userUid),
      where("orgUid", "==", orgUid),
    ),
  );

  if (querySnapshot.empty) {
    throw new Error("User-org relationship not found");
  }

  const docRef = querySnapshot.docs[0].ref;
  return deleteDoc(docRef);
};

/**
 * Get all users for a specific organization
 *
 * @param orgUid - Organization UID
 * @returns Array of user-org relationships
 */
export const getUsersForOrg = async (orgUid: string): Promise<UserOrg[]> => {
  const querySnapshot = await getDocs(
    query(
      collection(db, "jUserOrgs").withConverter(USER_ORG_CONVERTER),
      where("orgUid", "==", orgUid),
    ),
  );
  return querySnapshot.docs.map((doc) => doc.data());
};

/**
 * Get all organizations for a specific user
 *
 * @param userUid - User UID
 * @returns Array of user-org relationships
 */
export const getOrgsForUser = async (userUid: Uid): Promise<UserOrg[]> => {
  const querySnapshot = await getDocs(
    query(
      collection(db, "jUserOrgs").withConverter(USER_ORG_CONVERTER),
      where("userUid", "==", userUid),
    ),
  );
  return querySnapshot.docs.map((doc) => doc.data());
};

/**
 * Check if a user belongs to an organization
 *
 * @param userUid - User UID
 * @param orgUid - Organization UID
 * @returns true if user belongs to org, false otherwise
 */
export const isUserInOrg = async (
  userUid: Uid,
  orgUid: string,
): Promise<boolean> => {
  const relationship = await getUserOrgRelationship(userUid, orgUid);
  return relationship !== null && relationship.status === "active";
};

/**
 * Get users by role in an organization
 *
 * @param orgUid - Organization UID
 * @param role - Role to filter by
 * @returns Array of user-org relationships with the specified role
 */
export const getUsersByRole = async (
  orgUid: string,
  role: string,
): Promise<UserOrg[]> => {
  const querySnapshot = await getDocs(
    query(
      collection(db, "jUserOrgs").withConverter(USER_ORG_CONVERTER),
      where("orgUid", "==", orgUid),
      where("role", "==", role),
      where("status", "==", "active"),
    ),
  );
  return querySnapshot.docs.map((doc) => doc.data());
};
